import React from 'react'
import { Card, Row, Col, Skeleton } from 'antd'
import './energy-level-skeleton.less'

/**
 * Skeleton loading state for the Energy Level page
 * Uses skeleton-specific CSS classes to avoid conflicts with actual component styles
 */
const EnergyLevelSkeleton: React.FC = () => {
  return (
    <div className="energy-level-page">
      {/* Header banner */}
      <div className="energy-header-banner">
        <div className="energy-header-container center-content">
          <div className="energy-header-content">
            {/* Title uses h2 styling - margin-bottom: 4px from .energy-header-title */}
            <Skeleton.Input active size="large" className="energy-header-title" style={{ width: 380, height: 32 }} />
            {/* Subtitle uses Text styling */}
            <Skeleton.Input active size="small" className="energy-header-subtitle" style={{ width: 300, height: 18 }} />
          </div>
        </div>
      </div>

      <div className="energy-page-content center-content">
        {/* Info alert skeleton - margin-bottom matches .energy-info-banner (16px) */}
        <Row gutter={[16, 16]} style={{ marginBottom: 15 }}>
          <Col span={24}>
            <Skeleton.Input active block style={{ height: 40, borderRadius: 8 }} />
          </Col>
        </Row>

        {/* Main card - Energy gauge + Chart */}
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Card>
              <Row gutter={[16, 16]}>
                {/* Energy gauge - matches energy-gauge/index.tsx structure */}
                <Col xs={24} lg={8}>
                  <div className="energy-gauge">
                    {/* Header: margin-bottom: 24px (@spacing-xl) */}
                    <div className="energy-gauge-header">
                      <div className="energy-gauge-description">
                        <Skeleton.Input active size="small" style={{ width: 160, height: 14 }} />
                      </div>
                      {/* Title: Typography.Title level={3} */}
                      <Skeleton.Input active style={{ width: 110, height: 28 }} />
                    </div>
                    {/* Circle: margin-bottom: 16px (@spacing-lg) */}
                    <div className="energy-gauge-circle">
                      <div className="skeleton-battery" />
                    </div>
                    {/* Feedback: margin-top: 16px, gap: 8px */}
                    <div className="energy-gauge-feedback">
                      <div className="energy-gauge-feedback-text-container">
                        <div className="energy-gauge-feedback-text">
                          <Skeleton.Input active size="small" style={{ width: 200, height: 14 }} />
                        </div>
                        <div className="energy-gauge-feedback-subtext">
                          <Skeleton.Input active size="small" style={{ width: 120, height: 14 }} />
                        </div>
                      </div>
                      <Skeleton.Button active style={{ width: 130, height: 32 }} />
                    </div>
                  </div>
                </Col>

                {/* Trend chart - matches energy-trend-chart/index.tsx structure */}
                <Col xs={24} lg={16}>
                  <div className="energy-trend-chart">
                    {/* Title: Typography.Title level={4}, margin-bottom: 16px from CSS */}
                    <Skeleton.Input active style={{ width: 220, height: 24, marginBottom: 16 }} />
                    {/* Chart canvas */}
                    <div className="skeleton-chart" style={{ height: 340 }} />
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        {/* Bottom section */}
        <Row gutter={[16, 16]} className="energy-level-bottom-section" style={{ marginTop: 16 }}>
          {/* Score breakdown + Recharge Type (right side on desktop) */}
          <Col xs={24} xl={8} className="score-breakdown-col score-breakdown-col-responsive">
            <div className="score-breakdown-wrapper">
              {/* Score breakdown card - matches score-breakdown/index.tsx structure */}
              <Card className="score-breakdown-card">
                <div className="score-breakdown">
                  {/* Title (Typography.Title level={4}) - .ant-typography has margin-bottom: 16px */}
                  <Skeleton.Input active style={{ width: 180, height: 24, marginBottom: 15 }} />
                  <div className="header-separator" />
                  {/* Description - .score-breakdown-description has margin-bottom: 16px */}
                  <Skeleton.Input active size="small" block style={{ height: 16, marginBottom: 16 }} />
                  <div className="score-breakdown-table">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="score-breakdown-row">
                        <div className="score-breakdown-cell">
                          <Skeleton.Input active size="small" style={{ width: 100, height: 20 }} />
                        </div>
                        <div className="score-breakdown-cell progress-cell">
                          <Skeleton.Input active size="small" style={{ width: 80, height: 8 }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>

              {/* Recharge Type card */}
              <Card className="recharge-type-card">
                <div className="recharge-summary-card">
                  {/* Header: "Your Recharge Type" */}
                  <div className="recharge-summary-header">
                    <Skeleton.Input active style={{ width: 180, height: 22, marginBottom: 5 }} />
                  </div>
                  <div className="recharge-summary-divider" style={{marginBottom: 24}} />
                  {/* Type name: emoji + "Social Sprinter" */}
                  <div className="recharge-summary-type">
                    <Skeleton.Input active style={{ width: 160, height: 26, marginBottom: 4 }} />
                    {/* Tagline */}
                    <Skeleton.Input active size="small" style={{ width: 280, height: 16 }} />
                  </div>
                  {/* Tips section */}
                  <div className="recharge-summary-tips">
                    <Skeleton.Input active size="small" style={{ width: 80, height: 12, marginBottom: 12 }} />
                    <Skeleton.Input active size="small" block style={{ height: 16, marginBottom: 10 }} />
                    <Skeleton.Input active size="small" block style={{ height: 16, marginBottom: 10 }} />
                    <Skeleton.Input active size="small" style={{ width: '70%', height: 16 }} />
                  </div>
                  {/* Game CTA */}
                  <div className="recharge-summary-game-cta" style={{ marginTop: 16 }}>
                    <Skeleton.Input active size="small" style={{ width: 220, height: 14 }} />
                  </div>
                </div>
              </Card>
            </div>
          </Col>

          {/* Suggested actions (left side on desktop) */}
          <Col xs={24} xl={16} className="suggested-actions-col">
            <Card>
              <div className="suggested-actions">
                <div className="suggested-actions-header">
                  <div className="header-top-section">
                    <Skeleton.Input active style={{ width: 200, height: 24 }} />
                    <Skeleton.Button active size="small" style={{ width: 150, height: 28, borderRadius: 12 }} />
                  </div>
                </div>
                <div className="header-separator" style={{ marginTop: 12 }} />
                {/* Recharge type message - matches "Hey Social Sprinter..." paragraph */}
                <div className="recharge-type-message">
                  <Skeleton.Input active style={{ height: 20, marginBottom: 8 }} />
                </div>

                {/* Suggestion cards - matches suggested-action-card.tsx structure */}
                {[1, 2].map((i) => (
                  <div
                    key={i}
                    className={`skeleton-suggestion-card ${i === 1 ? 'skeleton-best-option' : ''}`}
                  >
                    <div className="skeleton-card-wrapper">
                      <div className="skeleton-card-content">
                        {/* CardHeader - date range title + status label */}
                        <div className="skeleton-card-header">
                          <Skeleton.Input active style={{ width: 180, height: 22 }} />
                          <Skeleton.Button active size="small" style={{ width: 80, height: 22, borderRadius: 12 }} />
                        </div>
                        {/* PTOSummary - "2 PTO days -> 6 days of rest" */}
                        <div>
                          <Skeleton.Input active size="small" style={{ width: 200, height: 14 }} />
                        </div>
                        {/* ActionDetails - list of details with icons */}
                        <div className="skeleton-action-details">
                          {[1, 2, 3, 4].map((j) => (
                            <div key={j} className="skeleton-detail-item">
                              <Skeleton.Avatar active size={14} />
                              <Skeleton.Input active size="small" style={{ width: j === 2 ? 320 : 200, height: 14 }} />
                            </div>
                          ))}
                        </div>
                        {/* ActionButtons - Request These Dates button */}
                        <div className="skeleton-action-buttons">
                          <Skeleton.Button active style={{ width: 150, height: 40, borderRadius: 8 }} />
                        </div>
                      </div>
                      <div className="skeleton-calendar">
                        <Skeleton.Input active size="small" style={{ width: 50, height: 14 }} />
                        <div className="skeleton-calendar-grid">
                          {Array.from({ length: 14 }).map((_, idx) => (
                            <Skeleton.Avatar key={idx} active size={32} shape="square" style={{ borderRadius: 6 }} />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default EnergyLevelSkeleton
